import { mapFormStateToCreateInput } from '@griply/services';
import { CalendarEventFormFields, ExternalCalendarEvent } from '@griply/types';
import {
  buildEventFormOptimisticEvent,
  getExternalEventBoundaryDate,
} from '@griply/utils';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { createExternalCalendarEvent } from 'shared/services/backend-api';
import { v4 as uuidv4 } from 'uuid';

import { queryKey as eventsQueryKey } from './use-external-calendar-events-query';

type CachedEvents = ExternalCalendarEvent[] | undefined;

// Query keys are [key, windowStartISO, windowEndISO, timezone]. Only the windows the new
// event actually falls in may paint it: the today list renders its window unfiltered, so
// seeding every cached window would show a next-week event under today. The bounds are
// `startOfDay(...).toISOString()` — local — so the boundaries must decode locally too.
const windowCoversEvent = (
  queryKey: readonly unknown[],
  event: ExternalCalendarEvent,
) => {
  const [, windowStart, windowEnd] = queryKey;
  if (typeof windowStart !== 'string' || typeof windowEnd !== 'string') {
    return false;
  }

  return (
    getExternalEventBoundaryDate(event.start) < new Date(windowEnd) &&
    getExternalEventBoundaryDate(event.end) > new Date(windowStart)
  );
};

export const useCreateExternalCalendarEventMutation = () => {
  const client = useQueryClient();

  return useMutation({
    mutationFn: async (fields: CalendarEventFormFields) =>
      createExternalCalendarEvent(mapFormStateToCreateInput(fields)),

    // These callables have no snapshot listener behind them, so without a placeholder the
    // new event stays invisible for the whole create round-trip plus the refetch.
    onMutate: async (fields) => {
      if (!fields.calendar) {
        return null;
      }

      const optimisticEvent: ExternalCalendarEvent = {
        ...buildEventFormOptimisticEvent(fields),
        id: uuidv4(),
        connectionId: fields.calendar.connectionId,
        calendarId: fields.calendar.calendarId,
        pending: true,
      };

      const previous = client.getQueriesData<ExternalCalendarEvent[]>({
        queryKey: [eventsQueryKey],
      });

      previous.forEach(([key, events]) => {
        if (!windowCoversEvent(key, optimisticEvent)) {
          return;
        }
        client.setQueryData<CachedEvents>(key, [
          ...(events ?? []),
          optimisticEvent,
        ]);
      });

      return { previous };
    },

    onError: (_error, _fields, context) => {
      context?.previous.forEach(([key, events]) =>
        client.setQueryData<CachedEvents>(key, events),
      );
    },

    onSettled: async () => {
      await client.invalidateQueries({ queryKey: [eventsQueryKey] });
    },
  });
};
